// MainContent component - dynamically renders content based on route
import logger from '../utils/logger.js';
import { isFeatureEnabled } from '../utils/feature-flags.js';
import { getGroupInviteToken } from '../utils/group-invite-route.js';
import {
  fetchPostStreamPage,
  focusGroupComment,
  focusGroupPost,
  waitForGroupShellFeedReady
} from '../utils/group-permalink.js';
import { readTabFromUrl, writeTabToUrl } from '../utils/tab-url.js';

const PUBLIC_CONTENT_ROUTES = ['/privacy', '/terms', '/release-notes'];
const FRIENDS_TABS = ['friends', 'requests', 'invitations'];
const FRIENDS_DEFAULT_TAB = 'friends';

class MainContent extends HTMLElement {
  constructor() {
    super();
    this.currentSection = null;
    this.currentContent = null;
    this.currentUser = null;
    this._pendingGroupCommentFocus = null;
    this._pendingGroupPostFocus = null;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    // Remove event listeners when component is removed
    document.removeEventListener('route-changed', this.handleRouteChange);
    document.removeEventListener('auth-success', this.handleAuthSuccess);
    document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    document.removeEventListener('user-logged-out', this.handleUserLoggedOut);
    document.removeEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
  }

  handleAuthSuccess(e) {
    const user = e.detail?.user || {};
    this.updateCurrentUser(user);
    this.render();
  }

  handleAuthStatusLoaded(e) {
    const { authenticated, user } = e.detail;
    if (authenticated) {
      this.updateCurrentUser(user);
      this.render();
    }
  }


  handleUserLoggedOut() {
    this.updateCurrentUser(null);
    this.render();
  }

  setupEventListeners() {
    this.handleRouteChange = this.handleRouteChange.bind(this);
    this.handleAuthSuccess = this.handleAuthSuccess.bind(this);
    this.handleAuthStatusLoaded = this.handleAuthStatusLoaded.bind(this);
    this.handleUserLoggedOut = this.handleUserLoggedOut.bind(this);
    this.handleFeatureFlagsLoaded = this.handleFeatureFlagsLoaded.bind(this);

    document.addEventListener('route-changed', this.handleRouteChange);
    document.addEventListener('auth-success', this.handleAuthSuccess);
    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    document.addEventListener('user-logged-out', this.handleUserLoggedOut);
    document.addEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
  }

  handleFeatureFlagsLoaded() {
    const route = window.router?.getCurrentRoute() || window.location.pathname;
    this.handleRouteChange(route);
  }

  render() {
    // Get current route from router
    const currentRoute = window.router?.getCurrentRoute() || window.location.pathname;
    this.handleRouteChange(currentRoute);
  }

  async handleRouteChange(routeEvent) {
    logger.info(`MainContent: Handling route change to ${routeEvent}`);

    const route = routeEvent?.detail?.route || routeEvent;

    // Clear current content
    this.clearContent();

    if (PUBLIC_CONTENT_ROUTES.includes(route)) {
      return;
    }

    const groupInviteToken = getGroupInviteToken(route);
    if (groupInviteToken) {
      await this.renderGroupInviteAccept(groupInviteToken);
      return;
    }

    if (!this.currentUser) {
      // eventually we should probably have some static information about the site
      return;
    }

    // Determine which content to show based on route
    if (route === '/' || route === '/home') {
      await this.renderHome();
    } else if (route === '/friends') {
      await this.renderFriends();
    } else if (route === '/profile') {
      await this.renderProfile();
    } else if (route === '/profile/me') {
      await this.renderUserProfile('me');
    } else if (route.startsWith('/profile/')) {
      const username = route.split('/')[2];
      await this.renderUserProfile(username);
    } else if (route === '/oauth2/authorize') {
      await this.renderOAuthAuthorize();
    } else if (route === '/admin') {
      await this.renderAdminSettings();
    } else if (route === '/admin/audit-logs') {
      await this.renderAdminAuditLogs();
    } else if (route === '/admin/users') {
      await this.renderAdminUsers();
    } else if (route === '/admin/prompts') {
      await this.renderAdminPrompts();
    } else if (route === '/admin/widgets') {
      await this.renderAdminWidgets();
    } else if (route === '/admin/stats') {
      await this.renderAdminStats();
    } else if (route === '/admin/image-flags') {
      await this.renderAdminImageFlags();
    } else if (route === '/admin/widget-flags') {
      await this.renderAdminWidgetFlags();
    } else if (route === '/admin/blocked-domains') {
      await this.renderAdminBlockedDomains();
    } else if (route === '/admin/oauth-clients') {
      await this.renderAdminOAuthClients();
    } else if (route === '/admin/feature-flags') {
      await this.renderAdminFeatureFlags();
    } else if (route === '/settings') {
      await this.renderSettings();
    } else if (route === '/settings/security') {
      await this.renderTwoFactorSettings();
    } else if (route === '/settings/notifications') {
      await this.renderNotificationSettings();
    } else if (route === '/settings/sparkle-sources') {
      await this.renderMediaSourcesSettings();
    } else if (route === '/settings/facebook-import-friends') {
      await this.renderFacebookImportFriends();
    } else if (route === '/settings/facebook-import-posts') {
      await this.renderFacebookImportPosts();
    } else if (route === '/settings/imported-friends') {
      await this.renderImportedFriends();
    } else if (route === '/settings/rss-feeds') {
      await this.renderRssFeeds();
    } else if (route === '/settings/widgets') {
      await this.renderWidgetSettings();
    } else if (route === '/memories' || route.startsWith('/memories/')) {
      const dateMatch = route.match(/^\/memories\/(\d{1,2}-\d{1,2})$/);
      const dateParam = dateMatch ? dateMatch[1] : null;
      await this.renderMemories(dateParam);
    } else if (route === '/events') {
      await this.renderEventsList();
    } else if (route === '/events/calendar') {
      await this.renderEventsCalendar();
    } else if (route === '/events/new') {
      const params = new URLSearchParams(window.location.search);
      const presetGroupId = params.get('groupId');
      await this.renderEventEdit(null, presetGroupId);
    } else if (route === '/mascot-parade') {
      if (!isFeatureEnabled('mascot_parade')) {
        window.router.navigate('/');
        return;
      }
      await this.renderMascotParade();
    } else if (route === '/widget-demo') {
      await this.renderWidgetDemo();
    } else if (route.match(/^\/group\/(\d+)\/post\/(\d+)\/comment\/(\d+)$/)) {
      const m = route.match(/^\/group\/(\d+)\/post\/(\d+)\/comment\/(\d+)$/);
      await this.renderGroupComment(m[1], m[2], m[3]);
    } else if (route.match(/^\/group\/(\d+)\/post\/(\d+)$/)) {
      const m = route.match(/^\/group\/(\d+)\/post\/(\d+)$/);
      await this.renderGroupPost(m[1], m[2]);
    } else if (route === '/groups') {
      await this.renderGroupsHub();
    } else if (route === '/groups/new') {
      await this.renderGroupCreate();
    } else if (route.match(/^\/groups\/(\d+)\/settings$/)) {
      const m = route.match(/^\/groups\/(\d+)\/settings$/);
      await this.renderGroupSettings(m[1]);
    } else if (route.match(/^\/groups\/(\d+)\/threads\/(\d+)$/)) {
      const m = route.match(/^\/groups\/(\d+)\/threads\/(\d+)$/);
      await this.renderGroupShell(m[1], m[2]);
    } else if (route.match(/^\/groups\/(\d+)$/)) {
      const m = route.match(/^\/groups\/(\d+)$/);
      await this.renderGroupShell(m[1], null);
    } else if (route.startsWith('/groups/')) {
      await this.renderNotFound();
    } else if (route.match(/^\/events\/\d+\/edit$/)) {
      const eventId = route.split('/')[2];
      await this.renderEventEdit(eventId);
    } else if (route.startsWith('/events/')) {
      const eventId = route.split('/')[2];
      await this.renderEventDetail(eventId);
    } else if (route.match(/^\/post\/\d+\/comment\/\d+$/)) {
      // Handle comment permalinks: /post/123/comment/456
      const pathParts = route.split('/');
      const postId = pathParts[2];
      const commentId = pathParts[4];
      await this.renderComment(postId, commentId);
    } else if (route.startsWith('/post/')) {
      const postId = route.split('/')[2];

      // Check if there's a hash fragment for a comment
      const hash = window.location.hash;
      if (hash && hash.startsWith('#comment-')) {
        const commentId = hash.replace('#comment-', '');
        await this.renderComment(postId, commentId);
      } else {
        await this.renderPost(postId);
      }
    } else {
      // Unknown route - show 404
      await this.renderNotFound();
    }
    this.setCurrentUserOnChildComponents(this.currentUser);
    await this.flushPendingGroupPermalinkFocus();
  }

  clearContent() {
    // Clean up event listeners before clearing content
    if (this.handleFriendRequestsUpdated) {
      document.removeEventListener('friend-requests-updated', this.handleFriendRequestsUpdated);
    }
    if (this.handleRequestAction) {
      document.removeEventListener('friend-request-accepted', this.handleRequestAction);
      document.removeEventListener('friend-request-declined', this.handleRequestAction);
    }

    this.innerHTML = '';
    this.currentSection = null;
    this.currentContent = null;
    this._pendingGroupCommentFocus = null;
    this._pendingGroupPostFocus = null;
  }

  async renderHome() {
    logger.debug('MainContent: Rendering home section');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="home-section">
          <div class="post-area">
            <home-promo-banner></home-promo-banner>
            <post-writer></post-writer>
          </div>
          <div class="posts-container">
            <upcoming-events-banner></upcoming-events-banner>
            <posts-feed></posts-feed>
          </div>
        </section>
      </div>
    `;

    this.currentSection = 'home';
    this.dispatchSectionShown('home');
  }

  async renderFriends() {
    logger.debug('MainContent: Rendering friends section');

    const activeTab = readTabFromUrl(FRIENDS_TABS, { defaultTab: FRIENDS_DEFAULT_TAB });

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="friends-section">
          <div class="friends-page">
            <add-friend-cta></add-friend-cta>
            <div class="friends-tabs">
              <button class="friends-tab ${activeTab === 'friends' ? 'active' : ''}" data-tab="friends">
                🎪 Friends
              </button>
              <button class="friends-tab ${activeTab === 'requests' ? 'active' : ''}" data-tab="requests">
                📥 Requests
              </button>
              <button class="friends-tab ${activeTab === 'invitations' ? 'active' : ''}" data-tab="invitations">
                📨 Invitations
              </button>
            </div>
            <div class="friends-tab-content">
              <div class="tab-panel ${activeTab === 'friends' ? 'active' : ''}" data-panel="friends">
                <friends-list></friends-list>
              </div>
              <div class="tab-panel ${activeTab === 'requests' ? 'active' : ''}" data-panel="requests">
                <friend-requests></friend-requests>
              </div>
              <div class="tab-panel ${activeTab === 'invitations' ? 'active' : ''}" data-panel="invitations">
                <invitations-list></invitations-list>
              </div>
            </div>
          </div>
        </section>
      </div>
    `;

    this.currentSection = 'friends';
    this.dispatchSectionShown('friends');
    this.attachFriendsTabListeners();
    this.attachFriendsRequestListeners();
  }

  switchFriendsTab(targetTab) {
    if (!FRIENDS_TABS.includes(targetTab)) {
      return;
    }

    writeTabToUrl(targetTab, { omitDefault: FRIENDS_DEFAULT_TAB });

    const tabs = this.querySelectorAll('.friends-tab');
    const panels = this.querySelectorAll('.tab-panel');

    tabs.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.tab === targetTab);
    });

    panels.forEach((panel) => {
      panel.classList.toggle('active', panel.dataset.panel === targetTab);
    });
  }

  attachFriendsTabListeners() {
    const tabs = this.querySelectorAll('.friends-tab');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.switchFriendsTab(tab.dataset.tab);
      });
    });
  }

  attachFriendsRequestListeners() {
    // Listen for friend request updates to show star indicator
    this.handleFriendRequestsUpdated = (event) => {
      const receivedCount = event.detail?.receivedCount || 0;
      this.updateRequestsTabIndicator(receivedCount);
    };

    document.addEventListener('friend-requests-updated', this.handleFriendRequestsUpdated);

    // Also listen for request acceptance/decline events to ensure we catch updates
    // even if the friend-requests-updated event was missed
    this.handleRequestAction = () => {
      // Wait a bit for the loadRequests() to complete, then check the count
      setTimeout(() => {
        this.checkFriendRequestsCount();
      }, 300);
    };

    document.addEventListener('friend-request-accepted', this.handleRequestAction);
    document.addEventListener('friend-request-declined', this.handleRequestAction);
    document.addEventListener('invitation-accepted', this.handleRequestAction);

    // Manually check for requests after a short delay to catch initial load
    // This handles the case where the event fires before the listener is attached
    setTimeout(() => {
      this.checkFriendRequestsCount();
    }, 1000);
  }

  updateRequestsTabIndicator(receivedCount) {
    const requestsTab = this.querySelector('.friends-tab[data-tab="requests"]');

    if (requestsTab) {
      if (receivedCount > 0) {
        // Add star if not already present
        if (!requestsTab.querySelector('.request-indicator')) {
          requestsTab.innerHTML = '📥 Requests <span class="request-indicator">⭐</span>';
        }
      } else {
        // Remove star if present
        const indicator = requestsTab.querySelector('.request-indicator');
        if (indicator) {
          requestsTab.innerHTML = '📥 Requests';
        }
      }
    }
  }

  async checkFriendRequestsCount() {
    try {
      const response = await window.API.request('/friends');
      const receivedCount = response.receivedRequests?.length || 0;
      this.updateRequestsTabIndicator(receivedCount);
    } catch (error) {
      window.logger.error('Failed to check friend requests count:', error);
    }
  }

  async renderProfile() {
    logger.debug('MainContent: Rendering profile section');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="profile-section">
          <user-profile></user-profile>
        </section>
      </div>
    `;

    this.currentSection = 'profile';
    this.dispatchSectionShown('profile');
  }

  async renderUserProfile(username) {
    logger.debug(`MainContent: Rendering user profile for ${username}`);

    // Use the same profile section but trigger the load event
    await this.renderProfile();

    // Give the component a moment to set up event listeners
    await new Promise(resolve => setTimeout(resolve, 10));

    const userProfile = this.querySelector('user-profile');
    if (userProfile && this.currentUser) {
      userProfile.setCurrentUser(this.currentUser);
    }

    // Trigger event to load the specific user profile
    const event = new CustomEvent('profile-load', {
      detail: { username }
    });
    document.dispatchEvent(event);
  }

  async renderAdminSettings() {
    logger.debug('MainContent: Rendering admin settings page');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-settings-section">
          <admin-settings-page></admin-settings-page>
        </section>
      </div>
    `;

    this.currentSection = 'admin-settings';
    this.dispatchSectionShown('admin-settings');
  }

  async renderAdminAuditLogs() {
    logger.debug('MainContent: Rendering admin audit logs');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-audit-logs-section">
          <audit-logs></audit-logs>
        </section>
      </div>
    `;

    this.currentSection = 'admin-audit-logs';
    this.dispatchSectionShown('admin-audit-logs');
  }

  async renderAdminUsers() {
    logger.debug('MainContent: Rendering admin user management');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-users-section">
          <user-management></user-management>
        </section>
      </div>
    `;

    this.currentSection = 'admin-users';
    this.dispatchSectionShown('admin-users');
    await new Promise(resolve => setTimeout(resolve, 10));
    const component = this.querySelector('user-management');
    if (component && this.currentUser) {
      component.setCurrentUser(this.currentUser);
    }
  }

  async renderAdminWidgets() {
    logger.debug('MainContent: Rendering admin widget management');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-widgets-section">
          <admin-widget-management></admin-widget-management>
        </section>
      </div>
    `;

    this.currentSection = 'admin-widgets';
    this.dispatchSectionShown('admin-widgets');
  }

  async renderAdminPrompts() {
    logger.debug('MainContent: Rendering admin prompt management');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-prompts-section">
          <prompt-management></prompt-management>
        </section>
      </div>
    `;

    this.currentSection = 'admin-prompts';
    this.dispatchSectionShown('admin-prompts');
  }

  async renderAdminStats() {
    logger.debug('MainContent: Rendering admin site stats');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-stats-section">
          <site-stats></site-stats>
        </section>
      </div>
    `;

    this.currentSection = 'admin-stats';
    this.dispatchSectionShown('admin-stats');
  }

  async renderAdminImageFlags() {
    logger.debug('MainContent: Rendering admin image flags');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-image-flags-section">
          <image-flags></image-flags>
        </section>
      </div>
    `;

    this.currentSection = 'admin-image-flags';
    this.dispatchSectionShown('admin-image-flags');
  }

  async renderAdminWidgetFlags() {
    logger.debug('MainContent: Rendering admin widget flags');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-widget-flags-section">
          <widget-flags></widget-flags>
        </section>
      </div>
    `;

    this.currentSection = 'admin-widget-flags';
    this.dispatchSectionShown('admin-widget-flags');
  }

  async renderAdminBlockedDomains() {
    logger.debug('MainContent: Rendering admin blocked domains');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-blocked-domains-section">
          <blocked-domains></blocked-domains>
        </section>
      </div>
    `;

    this.currentSection = 'admin-blocked-domains';
    this.dispatchSectionShown('admin-blocked-domains');
  }

  async renderAdminOAuthClients() {
    logger.debug('MainContent: Rendering admin OAuth clients');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-oauth-clients-section">
          <oauth-clients></oauth-clients>
        </section>
      </div>
    `;

    this.currentSection = 'admin-oauth-clients';
    this.dispatchSectionShown('admin-oauth-clients');
  }

  async renderAdminFeatureFlags() {
    logger.debug('MainContent: Rendering admin feature flags');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="admin-feature-flags-section">
          <admin-feature-flags></admin-feature-flags>
        </section>
      </div>
    `;

    this.currentSection = 'admin-feature-flags';
    this.dispatchSectionShown('admin-feature-flags');
  }

  async renderMascotParade() {
    logger.debug('MainContent: Rendering mascot parade');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="mascot-parade-section">
          <mascot-parade-page></mascot-parade-page>
        </section>
      </div>
    `;

    this.currentSection = 'mascot-parade';
    this.dispatchSectionShown('mascot-parade');
  }

  async renderWidgetDemo() {
    logger.debug('MainContent: Rendering widget demo');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="widget-demo-section">
          <widget-demo-page></widget-demo-page>
        </section>
      </div>
    `;

    this.currentSection = 'widget-demo';
    this.dispatchSectionShown('widget-demo');
  }

  async renderOAuthAuthorize() {
    logger.debug('MainContent: Rendering OAuth authorization page');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="oauth-authorize-section">
          <oauth-authorize></oauth-authorize>
        </section>
      </div>
    `;
  }

  async renderTwoFactorSettings() {
    logger.debug('MainContent: Rendering 2FA settings section');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="two-factor-settings-section">
          <two-factor-settings></two-factor-settings>
        </section>
      </div>
    `;

    this.currentSection = 'two-factor-settings';
    this.dispatchSectionShown('two-factor-settings');
  }

  async renderNotificationSettings() {
    logger.debug('MainContent: Rendering notification settings');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="notification-settings-section">
          <notification-settings></notification-settings>
        </section>
      </div>
    `;

    this.currentSection = 'notification-settings';
    this.dispatchSectionShown('notification-settings');
  }

  async renderMediaSourcesSettings() {
    logger.debug('MainContent: Rendering media sources settings');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="media-sources-settings-section">
          <media-sources-settings></media-sources-settings>
        </section>
      </div>
    `;

    this.currentSection = 'media-sources-settings';
    this.dispatchSectionShown('media-sources-settings');
  }

  async renderFacebookImportFriends() {
    logger.debug('MainContent: Rendering Facebook import friends');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="facebook-import-friends-section">
          <facebook-import-friends></facebook-import-friends>
        </section>
      </div>
    `;

    this.currentSection = 'facebook-import-friends';
    this.dispatchSectionShown('facebook-import-friends');
    await new Promise(resolve => setTimeout(resolve, 10));
    const component = this.querySelector('facebook-import-friends');
    if (component && this.currentUser) {
      component.setCurrentUser(this.currentUser);
    }
  }

  async renderFacebookImportPosts() {
    logger.debug('MainContent: Rendering Facebook import posts');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="facebook-import-posts-section">
          <facebook-import-posts></facebook-import-posts>
        </section>
      </div>
    `;

    this.currentSection = 'facebook-import-posts';
    this.dispatchSectionShown('facebook-import-posts');
    await new Promise(resolve => setTimeout(resolve, 10));
    const component = this.querySelector('facebook-import-posts');
    if (component && this.currentUser) {
      component.setCurrentUser(this.currentUser);
    }
  }

  async renderImportedFriends() {
    logger.debug('MainContent: Rendering imported friends');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="imported-friends-section">
          <imported-friends></imported-friends>
        </section>
      </div>
    `;

    this.currentSection = 'imported-friends';
    this.dispatchSectionShown('imported-friends');
  }

  async renderWidgetSettings() {
    logger.debug('MainContent: Rendering widget settings');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="widget-settings-section">
          <widget-management></widget-management>
        </section>
      </div>
    `;

    this.currentSection = 'widget-settings';
    this.dispatchSectionShown('widget-settings');
  }

  async renderRssFeeds() {
    logger.debug('MainContent: Rendering RSS feeds settings');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="rss-feeds-section">
          <rss-feeds></rss-feeds>
        </section>
      </div>
    `;

    this.currentSection = 'rss-feeds';
    this.dispatchSectionShown('rss-feeds');
  }

  async renderSettings() {
    logger.debug('MainContent: Rendering settings page');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="settings-section">
          <settings-page></settings-page>
        </section>
      </div>
    `;

    this.currentSection = 'settings';
    this.dispatchSectionShown('settings');
  }

  async renderMemories(dateParam = null) {
    logger.debug('MainContent: Rendering memories section', { dateParam });

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="memories-section">
          <memories-view ${dateParam ? `date="${dateParam}"` : ''}></memories-view>
        </section>
      </div>
    `;

    this.currentSection = 'memories';
    this.dispatchSectionShown('memories');
  }

  async renderPost(postId) {
    logger.debug(`MainContent: Rendering post ${postId}`);

    // For post view, we show the home section but with post detail
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="home-section">
          <div class="post-area" style="display: none;">
            <post-writer></post-writer>
          </div>
          <div class="posts-container">
            <posts-feed style="display: none;"></posts-feed>
            <post-detail post-id="${postId}" style="display: block;"></post-detail>
          </div>
        </section>
      </div>
    `;

    this.currentSection = 'home';
    this.dispatchSectionShown('home');
  }

  async renderComment(postId, commentId) {
    logger.debug(`MainContent: Rendering comment ${commentId} in post ${postId}`);

    try {
      // Show the post first
      await this.renderPost(postId);

      // Wait for the comments section to be available
      const commentsSection = await this.waitForElement('comments-section');
      if (!commentsSection) {
        logger.warn('Comments section not found');
        return;
      }

      // Trigger comment loading if not already loaded
      if (!commentsSection.commentsLoaded) {
        await commentsSection.loadComments();
      }

      // Wait for the specific comment element to be available
      const commentElement = await this.waitForElement(`[data-comment-id="${commentId}"]`);
      if (!commentElement) {
        logger.warn(`Comment ${commentId} not found in post ${postId}`);
        return;
      }

      // Scroll to the comment
      commentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add a highlight effect
      this.highlightComment(commentElement);
    } catch (error) {
      logger.error('Failed to load comment:', error);
    }
  }

  // Helper to wait for an element to appear in the DOM
  waitForElement(selector, timeout = 5000) {
    return new Promise((resolve) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout fallback
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
    });
  }

  // Helper to highlight a comment
  highlightComment(commentElement) {
    commentElement.classList.add('comment-highlight');

    setTimeout(() => {
      commentElement.classList.remove('comment-highlight');
    }, 3000);
  }

  dispatchSectionShown(sectionName) {
    const event = new CustomEvent('section-shown', {
      detail: { section: sectionName }
    });
    document.dispatchEvent(event);
  }

  // Public method to get current section
  getCurrentSection() {
    return this.currentSection;
  }

  // Public method to reset home view (for router compatibility)
  resetHomeView() {
    if (this.currentSection === 'home') {
      const postsFeed = document.querySelector('posts-feed');
      const postWriter = document.querySelector('post-writer');
      const postDetail = document.querySelector('post-detail');

      if (postsFeed) {
        postsFeed.style.display = 'block';
      }

      if (postWriter) {
        postWriter.style.display = 'block';
      }

      if (postDetail) {
        postDetail.style.display = 'none';
      }
    }
  }

  // Public method to hide post detail (for router compatibility)
  hidePostDetail() {
    const postDetail = document.querySelector('post-detail');
    if (postDetail) {
      postDetail.style.display = 'none';
    }
  }

  // Update current user on the currently visible component
  updateCurrentUser(user) {
    this.currentUser = user;
    logger.debug('MainContent: Updating current user', user);
    this.setCurrentUserOnChildComponents(user);
  }

  setCurrentUserOnChildComponents(user) {
    const componentNames = [
      'post-writer',
      'posts-feed',
      'upcoming-events-banner',
      'home-promo-banner',
      'add-friend-cta',
      'friends-list',
      'friend-requests',
      'admin-settings-page',
      'audit-logs',
      'user-management',
      'prompt-management',
      'admin-widget-management',
      'widget-management',
      'site-stats',
      'image-flags',
      'blocked-domains',
      'oauth-clients',
      'admin-feature-flags',
      'two-factor-settings',
      'notification-settings',
      'media-sources-settings',
      'settings-page',
      'event-detail',
      'event-edit',
      'events-calendar',
      'events-list',
      'groups-hub',
      'group-create',
      'group-shell',
      'add-group-cta',
      'user-profile'
    ];

    componentNames.forEach(name => {
      this.querySelectorAll(name).forEach(component => {
        if (typeof component.setCurrentUser === 'function') {
          component.setCurrentUser(user);
        }
      });
    });

    const visibleComponent = this.findVisibleComponent();
    if (visibleComponent && typeof visibleComponent.setCurrentUser === 'function') {
      visibleComponent.setCurrentUser(user);
    }
  }

  async renderEventsList() {
    logger.debug('MainContent: Rendering events list');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="events-section">
          <events-list></events-list>
        </section>
      </div>
    `;

    this.currentSection = 'events';
    this.dispatchSectionShown('events');
  }

  async renderEventsCalendar() {
    logger.debug('MainContent: Rendering events calendar');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="events-calendar-section">
          <events-calendar></events-calendar>
        </section>
      </div>
    `;

    this.currentSection = 'events-calendar';
    this.dispatchSectionShown('events-calendar');
  }

  async renderGroupsHub() {
    logger.debug('MainContent: Rendering groups hub');
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="groups-section">
          <groups-hub></groups-hub>
        </section>
      </div>
    `;
    this.currentSection = 'groups';
    this.dispatchSectionShown('groups');
  }

  async renderGroupCreate() {
    logger.debug('MainContent: Rendering group create');
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="group-create-section">
          <group-create></group-create>
        </section>
      </div>
    `;
    this.currentSection = 'group-create';
    this.dispatchSectionShown('group-create');
  }

  async renderGroupInviteAccept(token) {
    logger.debug(`MainContent: Rendering group invite accept ${token}`);
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="group-invite-accept-section">
          <group-invite-accept-page invite-token="${token}"></group-invite-accept-page>
        </section>
      </div>
    `;
    this.currentSection = 'group-invite-accept';
    this.dispatchSectionShown('group-invite-accept');
  }

  async renderGroupSettings(groupId) {
    logger.debug(`MainContent: Rendering group settings ${groupId}`);
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="group-settings-section">
          <group-settings-page group-id="${groupId}"></group-settings-page>
        </section>
      </div>
    `;
    this.currentSection = 'group-settings';
    this.dispatchSectionShown('group-settings');
  }

  async renderGroupComment(groupId, postId, commentId) {
    logger.debug(`MainContent: Setting up group comment permalink ${commentId} on post ${postId} in group ${groupId}`);

    try {
      const gid = Number(groupId);
      const pid = Number(postId);
      const cid = Number(commentId);
      const location = await fetchPostStreamPage(gid, pid);
      await this.renderGroupShell(gid, location.threadId, { initialPage: location.page });
      this._pendingGroupCommentFocus = { groupId: gid, postId: pid, commentId: cid, location };
    } catch (error) {
      logger.error('Failed to load group comment:', error);
    }
  }

  async renderGroupPost(groupId, postId) {
    logger.debug(`MainContent: Setting up group post permalink ${postId} in group ${groupId}`);

    try {
      const gid = Number(groupId);
      const pid = Number(postId);
      const location = await fetchPostStreamPage(gid, pid);
      await this.renderGroupShell(gid, location.threadId, { initialPage: location.page });
      this._pendingGroupPostFocus = { groupId: gid, postId: pid, location };
    } catch (error) {
      logger.error('Failed to load group post:', error);
    }
  }

  async flushPendingGroupPermalinkFocus() {
    if (this._pendingGroupCommentFocus) {
      const pending = this._pendingGroupCommentFocus;
      this._pendingGroupCommentFocus = null;
      try {
        await waitForGroupShellFeedReady();
        await focusGroupComment(pending);
      } catch (error) {
        logger.error('Failed to focus group comment:', error);
      }
    }

    if (this._pendingGroupPostFocus) {
      const pending = this._pendingGroupPostFocus;
      this._pendingGroupPostFocus = null;
      try {
        await waitForGroupShellFeedReady();
        await focusGroupPost(pending);
      } catch (error) {
        logger.error('Failed to focus group post:', error);
      }
    }
  }

  async renderGroupShell(groupId, threadId, { initialPage } = {}) {
    logger.debug(`MainContent: Rendering group shell ${groupId} thread ${threadId}`);
    const threadAttr = threadId !== null && threadId !== undefined && threadId !== '' ? ` thread-id="${threadId}"` : '';
    const pageAttr = initialPage && initialPage > 1 ? ` initial-page="${initialPage}"` : '';
    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="group-shell-section">
          <group-shell group-id="${groupId}"${threadAttr}${pageAttr}></group-shell>
        </section>
      </div>
    `;
    this.currentSection = 'group-shell';
    this.dispatchSectionShown('group-shell');
  }

  async renderEventDetail(eventId) {
    logger.debug(`MainContent: Rendering event detail ${eventId}`);

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="event-detail-section">
          <event-detail event-id="${eventId}"></event-detail>
        </section>
      </div>
    `;

    this.currentSection = 'event-detail';
    this.dispatchSectionShown('event-detail');
  }

  async renderEventEdit(eventId = null, presetGroupId = null) {
    logger.debug(`MainContent: Rendering event ${eventId ? 'edit' : 'create'}`);

    const presetAttr = presetGroupId ? ` preset-group-id="${presetGroupId}"` : '';

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="event-edit-section">
          <event-edit${eventId ? ` event-id="${eventId}"` : ''}${presetAttr}></event-edit>
        </section>
      </div>
    `;

    this.currentSection = 'event-edit';
    this.dispatchSectionShown('event-edit');
  }

  async renderNotFound() {
    logger.debug('MainContent: Rendering 404 not found page');

    this.innerHTML = `
      <div class="container">
        <section class="content-section" id="not-found-section">
          <not-found></not-found>
        </section>
      </div>
    `;

    this.currentSection = 'not-found';
    this.dispatchSectionShown('not-found');
  }

  // Find the currently visible component that might need user updates
  findVisibleComponent() {
    // Look for common components that might need user updates
    const components = [
      'user-profile',
      'posts-feed',
      'post-writer',
      'friends-list',
      'friend-requests',
      'admin-dashboard',
      'admin-feature-flags',
      'two-factor-settings',
      'notification-settings',
      'settings-page',
      'event-detail',
      'event-edit',
      'events-calendar',
      'groups-hub',
      'group-create',
      'group-shell'
    ];

    for (const componentName of components) {
      const component = this.querySelector(componentName);
      if (component && component.offsetParent !== null) { // Check if visible
        return component;
      }
    }

    // Fallback: return the first visible component
    const allComponents = this.querySelectorAll('*');
    for (const component of allComponents) {
      if (component.offsetParent !== null && component.tagName.includes('-')) {
        return component;
      }
    }

    return null;
  }
}

// Register the custom element
customElements.define('main-content', MainContent);

export default MainContent;
